import React, { Children, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useIntersection } from 'react-use';

import IconSimpleArrowLeft from 'components/icons/iconSimpleArrowLeft';
import Link from 'components/consumer/Link';
import { prefersReducedMotion } from 'hooks/useScrollSelectedIntoView';

const ArrowButton = ({ direction, disabled, onClick }) => (
  <button
    type="button"
    aria-label={direction === 'prev' ? 'Previous' : 'Next'}
    disabled={disabled}
    onClick={onClick}
    className={classNames(
      'flex h-9 w-9 items-center justify-center border border-solid bg-white p-0 text-gray',
      'hover:cursor-pointer disabled:cursor-default disabled:opacity-50'
    )}
  >
    <IconSimpleArrowLeft
      stroke="currentColor"
      width={8}
      height={13}
      className={direction === 'next' ? 'rotate-180' : undefined}
    />
  </button>
);

ArrowButton.propTypes = {
  direction: PropTypes.oneOf(['prev', 'next']).isRequired,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
};

// Layout revamp bottom sections share this instead of CardSlider
const PDPSectionCarousel = ({ children, title, titleLink = null }) => {
  // State, not refs, so the observers re-create when the nodes change
  const [track, setTrack] = useState(null);
  const [first, setFirst] = useState(null);
  const [last, setLast] = useState(null);

  const firstEntry = useIntersection(
    { current: first },
    { root: track, threshold: 0.99 }
  );
  const lastEntry = useIntersection(
    { current: last },
    { root: track, threshold: 0.99 }
  );
  // isIntersecting is true at any overlap, so read the ratio
  const atStart = firstEntry ? firstEntry.intersectionRatio >= 0.99 : true;
  const atEnd = lastEntry ? lastEntry.intersectionRatio >= 0.99 : true;
  const lastIndex = Children.count(children) - 1;

  const scrollByPage = direction => {
    track?.scrollBy({
      left: direction * track.clientWidth,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  const heading = (
    <h2 className="m-0 text-center text-xl font-bold">{title}</h2>
  );

  return (
    <div className="w-full">
      {titleLink ? (
        <Link to={titleLink} className="block no-underline hover:underline">
          {heading}
        </Link>
      ) : (
        heading
      )}

      <div className="mt-6 flex justify-end gap-2">
        <ArrowButton
          direction="prev"
          disabled={atStart}
          onClick={() => scrollByPage(-1)}
        />
        <ArrowButton
          direction="next"
          disabled={atEnd}
          onClick={() => scrollByPage(1)}
        />
      </div>

      <div
        ref={setTrack}
        className="mt-4 flex snap-x snap-mandatory gap-6 overflow-x-auto motion-safe:scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {Children.map(children, (child, index) => (
          <div
            ref={index === 0 ? setFirst : index === lastIndex ? setLast : null}
            className="w-full flex-none snap-start md:w-[calc((100%_-_1.5rem)_/_2)] lg:w-[calc((100%_-_4.5rem)_/_4)]"
          >
            {child}
          </div>
        ))}
      </div>
    </div>
  );
};

PDPSectionCarousel.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string.isRequired,
  titleLink: PropTypes.string,
};

export default PDPSectionCarousel;
